import { useTranslation } from "react-i18next";
import type { ContentLibraryItem } from "@/hooks/useContentLibrary";
import type { RecordingOpts } from "@/components/HomeFeed";
import type { CardContext } from "@/types/cardContext";
import {
  CARD_COLORS,
  bodyStyle,
  cardShellStyle,
  fullCtaStyle,
  labelStyle,
  titleStyle,
} from "./types";

interface TipCardProps {
  tip: ContentLibraryItem;
  onRecord: (opts?: RecordingOpts) => void;
  onOpenCollectionPreview?: (templateId: string) => void;
  isCollectionInProgress?: boolean;
}

const TipCard = ({ tip, onRecord, onOpenCollectionPreview, isCollectionInProgress }: TipCardProps) => {
  const { t } = useTranslation();
  const colors = CARD_COLORS.tip;
  const title = tip.title ?? t("cards.tip.label");

  return (
    <div style={cardShellStyle(colors.bg, colors.border)}>
      {/* Top: label + title + body */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={labelStyle(colors.body)}>{t("cards.tip.label")}</div>

        {tip.subject === 'specific_child' && tip._resolvedChildName && (
          <div style={{ ...labelStyle(colors.body), marginTop: 4 }}>
            {tip._resolvedChildName}
          </div>
        )}

        <p
          style={{
            ...titleStyle(colors.title),
            marginTop: 6,
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </p>

        <p
          style={{
            ...bodyStyle(colors.body),
            marginTop: 4,
            display: "-webkit-box",
            WebkitLineClamp: 6,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flex: 1,
          }}
        >
          {tip.body}
        </p>
      </div>

      {/* Collection badge — only when card is a step in a journey */}
      {tip._collectionContext && (() => {
        const ctx = tip._collectionContext!;
        return (
          <div style={{ paddingTop: 6, borderTop: `1px solid ${colors.border}`, flexShrink: 0 }}>
            <p style={{ fontSize: 11, color: colors.body, opacity: 0.75, margin: 0 }}>
              {t("cards.collection.badge", { collectionName: ctx.templateName })}
            </p>
            <button
              type="button"
              onClick={() => onOpenCollectionPreview?.(ctx.templateId)}
              style={{ background: "none", border: "none", padding: "2px 0 0", fontSize: 11, fontWeight: 600, color: colors.title, cursor: "pointer" }}
            >
              {isCollectionInProgress ? t("cards.collection.continueJourney") : t("cards.collection.startJourney")}
            </button>
          </div>
        );
      })()}

      {/* Bottom: CTA */}
      <div style={{ paddingTop: 8 }}>
        <button
          type="button"
          onClick={() => {
            const captureType = tip.capture_type ?? "voice";
            const cardContext: CardContext = {
              contentLibraryId: tip.id,
              title,
              body: tip.body,
              instructions: tip.instructions ?? undefined,
              tip: tip.tip ?? undefined,
              captureType,
              tags: tip.tags ?? [],
              cardType: tip.card_type,
              voiceRequired: tip.voice_required,
            };
            onRecord({
              contextPrompt: title,
              captureType: captureType as "voice" | "drawing" | "photo" | "written" | "video",
              cardContext,
              voiceRequired: tip.voice_required,
            });
          }}
          style={fullCtaStyle(colors.title, colors.bg)}
        >
          {t("cards.tip.cta")}
        </button>
      </div>
    </div>
  );
};

export default TipCard;
